import { AwesomeEvent, ComponentAPI } from '@philips-emr/tws-core';
import Navigable, { NavigableEvent, NavigableEventListener } from '@philips-emr/tws-core/lib/component/types/navigable';
import Renderable, { RenderEventListener } from '@philips-emr/tws-core/lib/component/types/renderable';
import Submitable, { SubmitEventListener } from '@philips-emr/tws-core/lib/component/types/submitable';
import { editStep, getStepIdentifiers, noop, updateStepStages } from '@philips-emr/tws-utils';

interface Step {
  current: boolean;
  done: boolean;
  enabled: boolean;
  identifier: string;
  last: boolean;
}

class StepsAPI extends ComponentAPI implements Renderable, Submitable, Navigable<string> {
  /* @event */
  @AwesomeEvent
  onDynamicContentRender = new RenderEventListener();

  /* @event */
  @AwesomeEvent
  onBeforeSubmit = new SubmitEventListener();

  /* @event */
  @AwesomeEvent
  onSubmit = new SubmitEventListener();

  /* @event */
  @AwesomeEvent
  onBeforeCancel = new SubmitEventListener();

  /* @event */
  @AwesomeEvent
  onCancel = new SubmitEventListener();

  /* @event */
  @AwesomeEvent
  onAfterNavigate = new NavigableEventListener<string>();

  /* @event */
  @AwesomeEvent
  onNavigate = new NavigableEventListener<string>();

  private getStep(identifier: string): Step | undefined {
    const { steps } = this.state;

    return steps[identifier];
  }

  getClassName(): string {
    return this.state.className;
  }

  setClassName(className: string): void {
    this.setState({ className });
  }

  getConfirmExpression(): string {
    return this.state.confirmExpression;
  }

  setConfirmExpression(confirmExpression: string): void {
    this.setState({ confirmExpression });
  }

  getCurrentStepIdentifier(): string {
    return this.state.currentStep;
  }

  getNextExpression(): string {
    return this.state.nextExpression;
  }

  setNextExpression(nextExpression: string): void {
    this.setState({ nextExpression });
  }

  isStepDone(identifier: string): boolean {
    const step = this.getStep(identifier);

    return step ? step.done : false;
  }

  setStepDone(identifier: string, done: boolean): void {
    const { steps } = this.state;

    this.setState({
      steps: { ...steps, [identifier]: { ...steps[identifier], done } },
    });
  }

  submit(): void {
    this.onSubmit.fire();
  }

  cancel(): void {
    this.onCancel.fire();
  }

  getSteps(): Record<string, Step> {
    return this.state.steps;
  }

  setHiddenSteps(hiddenSteps: string[]): void {
    this.setState({ hiddenSteps });
  }

  getHiddenSteps(): string[] {
    return this.state.hiddenSteps;
  }

  editStep(identifier: string): void {
    const currentIdentifier = this.getCurrentStepIdentifier();

    if (identifier === currentIdentifier) {
      return;
    }

    const identifiers = getStepIdentifiers(this.getSteps());
    const currentIndex = identifiers.findIndex((id) => id === currentIdentifier);
    const expectedIndex = identifiers.findIndex((id) => id === identifier);

    if (expectedIndex < 0) {
      throw new Error(`[Steps API]: Could not find step with identifier "${identifier}".`);
    }

    if (expectedIndex > currentIndex) {
      throw new Error(`[Steps API]: Can't go to a following step.`);
    }

    const result = editStep(currentIdentifier, identifier, this.getSteps());

    this.setState({
      currentStep: result.identifier,
      steps: updateStepStages(result.identifier, result.steps),
      previousStep: currentIdentifier,
    });

    this.onNavigate.fire(new NavigableEvent({ abort: noop, from: currentIdentifier, to: result.identifier }));
    this.onAfterNavigate.fire(new NavigableEvent({ abort: noop, from: currentIdentifier, to: result.identifier }));
  }
}

export default StepsAPI;
